/**
 * The customer ordering page: handover rev7 §5.1–§5.12, ported from the
 * prototype vB6 (`build/PLT_catering_prototype_vB6_20261004.html`).
 *
 * Four steps, the §5.3 filters with their second row of subgroup chips, the
 * gluten-free dishes Planteen adds shown ticked, *Többet kérek ebből*, the
 * savoury/sweet balance in step 01 — and **no *Mennyiség* card and no
 * quantity level**, both retired by Barna on 4 October — the quiet sticky
 * summary, the Összesítés with category pricing, buyer-type gross or net, the
 * enquiry route, the callback link, and an EN switch with every string
 * translated. The quiet sticky summary of §5.4 became the offer card of
 * `build/FIXES.md` item 76 in `082_offer_first`, which carries every one of
 * its rows and adds the offer above them.
 *
 * What changed in the port, and nothing else did: the catalogue, the
 * parameters, the calendar and the capacity come from
 * `public.menu_context()`; the arithmetic is `packages/engine`, the same
 * `priceOrder()` the server re-runs; and the submission goes to
 * `submit_order`, `submit_enquiry` and `submit_callback`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { BRAND_TITLE, brandTitle } from '@plt/emails'

import { readPublicConfig } from '../lib/env.ts'
import { submitCallback, submitEnquiry, submitOrder, type OrderPayload } from '../lib/submit.ts'
import { simplePayCheck, startPayment } from '../lib/payment.ts'
import type { PageBundle, Patch } from './bundle.ts'
import { emailOk, formatter, phoneOk } from './format.ts'
import { useBrandMeta } from './brandMeta.ts'
import { langFromSearch, useDocumentLang } from './documentLang.ts'
import { translator, type Bilingual, type Lang } from './lang.ts'
import {
  availableDishes,
  availableFruit,
  calendarOf,
  coffeeOffer,
  coffeeServiceLines,
  deliveryBlocked,
  drinkDishes,
  engineParams,
  groupsOf,
  limitsOf,
  MEAL_DEFAULT_FROM_PARAM,
  isReception,
  mealReplacementDefault,
  occasionForMeal,
  occasionOf,
  offeredSlots,
  priceThePage,
  refreshmentDrinkOffer,
  offerIsReady,
  outsideDeliveryRadius,
  resolveSelection,
  rulesOf,
  fulfilmentWindow,
  toMenuDish,
  totalsOf,
} from './model.ts'
import { menuCheckBandOf, menuCheckOf } from './menuCheck.ts'
import { Step1Event } from './sections/Step1Event.tsx'
import { Step2Menu } from './sections/Step2Menu.tsx'
import { Step3Contact } from './sections/Step3Contact.tsx'
import { dijbekeroRoute, simplePayRoute, Step4Summary } from './sections/Step4Summary.tsx'
import { LegalFooter } from './LegalFooter.tsx'
import { OfferBar, OfferCard } from './sections/Offer.tsx'
import { Banner, Header, Hero, Rail, Strip, TrustBar, Zoom } from './shell.tsx'
import { advanceOpening, type OpeningState } from './opening.ts'
import { initialState, type PageState } from './state.ts'
import { loadTownTable, withPostcode, type TownTable } from './towns.ts'
import { clearSettledFlags, firstMissingField, jumpToField, missingItems } from './missing.ts'
import { sectionTicks } from './rail.ts'
/* Run 19 §9: the fixed bars get out of the keyboard's way while a field on a
   narrow screen has focus. */
import { installKeyboardHide } from './typing.ts'
import { askablePostcode, serverSentence, useMenuContext } from './usePageData.ts'

export function CustomerPage(): JSX.Element {
  const config = useMemo(() => readPublicConfig(), [])
  const navigate = useNavigate()
  /* §5.12: Hungarian is the page's language. `?lang=en` is how the §9.4
     return page hands an English customer back, so the first state reads it;
     after that the header switch owns the language, as it did in vB6. */
  const [search] = useSearchParams()
  const [state, setState] = useState<PageState>(() => initialState(langFromSearch(search)))

  const set = useCallback((patch: Patch) => {
    setState((current) => ({ ...current, ...(typeof patch === 'function' ? patch(current) : patch) }))
  }, [])

  const reading = useMenuContext(config, {
    date: state.date === '' ? null : state.date,
    start: state.start,
    postcode: askablePostcode(state.postcode),
    fulfilment: state.fulfilment,
  })
  const context = reading.context

  /* Specification §3.7: the postcode fills in the town. The table loads once,
     on its own; when it arrives, an address whose postcode was typed before
     it did gets its town now — never one the customer typed. */
  const [towns, setTowns] = useState<TownTable | null>(null)
  useEffect(() => {
    let live = true
    void loadTownTable().then((table) => {
      if (!live) return
      setTowns(table)
      setState((current) => ({
        ...current,
        billing: withPostcode(current.billing, current.billing.postcode, table),
        shipping: withPostcode(current.shipping, current.shipping.postcode, table),
      }))
    })
    return () => {
      live = false
    }
  }, [])

  /* §5.12: the tab title and the document language are page strings too. */
  useDocumentLang(state.lang)
  /* DECIDED 20261019, Barna: the whole title is `BRAND_TITLE`, not a word
     this file builds a title around. */
  useEffect(() => {
    document.title = brandTitle(state.lang)
  }, [state.lang])
  /* DECIDED 20261020: the description and og: tags turn with it. */
  useBrandMeta(state.lang)

  /* Run 19 §9 (DECIDED 20261016, Barna): while a text or number field has
     focus on a narrow screen, the fixed bottom bar and the sticky header get
     out of the way of the keyboard. One attribute on `<html>`, so nothing
     re-renders while somebody is typing; the stylesheet does the rest.
     Desktop is unchanged, which `narrowScreen` is what decides. */
  useEffect(() => installKeyboardHide(document, window), [])

  /* build/FIXES.md item 31: one offset — the header height plus 12px — which
     html's scroll-padding-top, the step rail and the sticky summary all read.
     Measured rather than written down, because the header wraps at narrow
     widths, grows with the tap-target floor of item 26 and changes with the
     language. */
  useEffect(() => {
    const setJump = (): void => {
      const header = document.querySelector('.top')
      const bottom = header?.getBoundingClientRect().bottom ?? 0
      /* Specification §3.1: on one column the rail is a slim line stuck
         under the header, so the offset grows by its height and a jump lands
         below it rather than under it. `--head` is where the line sticks. */
      const line = document.getElementById('railLine')?.getBoundingClientRect().height ?? 0
      if (bottom > 0) {
        document.documentElement.style.setProperty('--head', `${Math.round(bottom)}px`)
        document.documentElement.style.setProperty('--jump', `${Math.round(bottom + line + 12)}px`)
      }
    }
    setJump()
    window.addEventListener('resize', setJump)
    void document.fonts?.ready.then(setJump)
    return () => window.removeEventListener('resize', setJump)
  })

  /* The page opens on a date the calendar can take (§8.1), which the server
     works out: vB6 worked it out in the browser from a hard-coded holiday
     list and this page has no such list.
     Once, and only once. A customer may clear the date — §5.2 allows it and
     §11.3 answers it with *Nincs megadva dátum.* — and an effect that put
     the suggestion back every time the field was empty would make that
     impossible. */
  const suggested = useRef(false)
  /* …and on one the slot rules accept (Barna, 10 October 2026): from the
     suggestion, the first weekday where 12:00 has a delivery slot. `opening`
     holds the date the page itself put in the field; any other date there
     means the customer has chosen, and the search stops for good. */
  const opening = useRef<OpeningState>({
    active: false,
    date: null,
    steps: 0,
  })
  useEffect(() => {
    if (suggested.current || state.date !== '' || context?.suggested_event_date == null) return
    suggested.current = true
    opening.current = { active: true, date: context.suggested_event_date, steps: 0 }
    set({ date: context.suggested_event_date })
  }, [context?.suggested_event_date, state.date, set])
  useEffect(() => {
    const { next, setDate } = advanceOpening(opening.current, {
      date: state.date,
      start: state.start,
      fulfilment: state.fulfilment,
      context,
      fresh: !reading.stale && !reading.loading,
    })
    opening.current = next
    if (setDate !== null) set({ date: setDate })
  }, [context, reading.stale, reading.loading, state.date, state.start, state.fulfilment, set])

  /* §8.1: a weekend event is pickup only. Decided before the figures are
     worked out, not while they are being drawn — vB6 used to set it after the
     totals had already charged a delivery fee for a day it does not deliver
     on. */
  const slots = context === null ? [] : offeredSlots(context)
  const weekendDay = /^\d{4}-\d{2}-\d{2}$/.test(state.date)
    ? new Date(`${state.date}T12:00`).getDay()
    : -1
  useEffect(() => {
    if ((weekendDay === 0 || weekendDay === 6) && state.fulfilment !== 'pickup') {
      set({ fulfilment: 'pickup' })
    }
  }, [weekendDay, state.fulfilment, set])

  if (context === null) {
    return (
      <>
        <Loading lang={state.lang} error={reading.error} />
        <LegalFooter lang={state.lang} />
      </>
    )
  }

  return (
    <Loaded
      state={state}
      set={set}
      context={context}
      stale={reading.stale}
      reloadMenu={reading.reload}
      config={config}
      slots={slots}
      navigate={navigate}
      towns={towns}
    />
  )
}

/**
 * §5.12: the sentence shown when a submission did not go through.
 *
 * `submit_order` and its two siblings answer with one plain Hungarian
 * sentence — release one is Hungarian only — so a sentence that came back is
 * carried in both halves of the pair unchanged. When nothing came back at
 * all, the page says so in the language on screen, and it stays a pair so the
 * EN switch turns it while the note is up.
 */
const SUBMIT_UNAVAILABLE: Bilingual = {
  hu: 'A kérést most nem tudjuk feldolgozni. Kérjük, próbálja meg újra.',
  en: 'We cannot process your request just now. Please try again.',
}

/**
 * 25 October 2026: the menu changed while the order was being submitted, and
 * the server's one re-price did not catch up with it. The wording is the run
 * prompt's (docs/PLT_catering_create_order_retry_loop_prompt_20261024.md),
 * with the formal "nézze át" (Barna, 1 November 2026) in place of the
 * prompt's informal "nézd át". The page re-reads the menu and waits for the customer to
 * submit again: it never resubmits by itself.
 */
export const MENU_CHANGED: Bilingual = {
  hu: 'A kínálat időközben frissült, kérjük, nézze át az ajánlatot.',
  en: 'The menu has just been updated, please review your offer.',
}

const sentenceFor = (answer: { message?: string }): Bilingual =>
  answer.message === undefined ? SUBMIT_UNAVAILABLE : serverSentence(answer.message)

function Loading(props: { lang: Lang; error: Bilingual | null }): JSX.Element {
  const t = translator(props.lang)
  return (
    <main className="payshell" data-testid="order-page-loading">
      {/* §14: the Hungarian article belongs to the Hungarian, and the name is
          spelled here exactly as the tab title and `index.html` spell it —
          the same `BRAND_TITLE` string, so there is no second spelling. In
          English it is The Planteen. */}
      <h1>{t(BRAND_TITLE.hu, BRAND_TITLE.en)}</h1>
      {props.error === null ? (
        <p>{t('Betöltjük a kínálatot…', 'Loading the menu…')}</p>
      ) : (
        <div className="note stop">{t.of(props.error)}</div>
      )}
    </main>
  )
}

/**
 * The page with a reading in hand. Split out so the hooks above never run
 * conditionally and the whole bundle below is built from a context that
 * exists.
 */
function Loaded(props: {
  state: PageState
  set: (patch: Patch) => void
  context: NonNullable<ReturnType<typeof useMenuContext>['context']>
  stale: boolean
  reloadMenu: () => void
  config: ReturnType<typeof readPublicConfig>
  slots: ReturnType<typeof offeredSlots>
  navigate: ReturnType<typeof useNavigate>
  towns: TownTable | null
}): JSX.Element {
  const { state, set, context, stale, reloadMenu, config, slots, navigate, towns } = props
  const t = useMemo(() => translator(state.lang), [state.lang])
  const f = useMemo(() => formatter(state.lang), [state.lang])

  const param = useCallback((key: string) => String(context.params[key] ?? ''), [context])
  const num = useCallback((key: string) => Number(context.params[key] ?? 0), [context])

  /* SimplePay test prompt of 3 November 2026, §1: on the díjbekérő route the
     server says, true or false, whether the email typed takes the SimplePay
     test route. Asked once the address is well formed and has stopped
     changing; the list itself never reaches the page. Under `simplepay`
     nothing is asked: every buyer is on the SimplePay route. */
  const typedEmail = state.email.trim()
  const askSimplePay = dijbekeroRoute(param) && emailOk(typedEmail)
  useEffect(() => {
    if (!askSimplePay) {
      if (state.simplepayTest) set({ simplepayTest: false, acceptSimplePay: false })
      return
    }
    let stale = false
    const timer = window.setTimeout(() => {
      void simplePayCheck(config, typedEmail).then((yes) => {
        if (!stale && yes !== state.simplepayTest) set({ simplepayTest: yes, acceptSimplePay: false })
      })
    }, 400)
    return () => {
      stale = true
      window.clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askSimplePay, typedEmail, config])
  const flag = useCallback(
    (key: string) => String(context.params[key] ?? '').trim().toUpperCase() === 'TRUE',
    [context],
  )

  const dishes = useMemo(() => context.dishes.map(toMenuDish), [context])
  const groups = useMemo(() => groupsOf(context.menu_groups), [context])
  const params = useMemo(() => engineParams(context), [context])
  const calendar = useMemo(() => calendarOf(context, state, slots), [context, state, slots])
  const available = useMemo(() => availableDishes(dishes, calendar.lane), [dishes, calendar.lane])
  const fruitAvailable = useMemo(() => availableFruit(dishes, calendar.lane), [dishes, calendar.lane])
  const drinks = useMemo(() => drinkDishes(dishes), [dishes])

  /* `build/FIXES.md` item 73 (F2): the conference holds no piece figures of
     its own, so the three answers go in with the key and the occasion is
     built out of the Kávészünet and Munkaebéd rows of this very reading. */
  const occasion = useMemo(
    () =>
      occasionOf(context, state.occasion, state.conference) ??
      occasionOf(context, context.occasions[0]?.key ?? '', state.conference),
    [context, state.occasion, state.conference],
  )

  const guests = Math.min(400, Math.max(1, Math.round(Number(state.guestsField) || 1)))

  /* Run 17 item B4: the band inside which the menu reads as *megfelelo*
     against the recommendation, out of the register and never a literal. An
     empty register value leaves the band at nothing, which makes any
     difference at all read as above or below rather than silently as right. */
  const menuCheckBand = menuCheckBandOf(param)

  /* `build/FIXES.md` items 74 and 78 (F3 and F7). Both are null today and
     both are meant to be: F3 waits on a unit volume on the drink rows and F7
     on an Americano catalogue row and `coffee_per_head_per_occasion`. Null is
     what hides the line and the question. */
  const refreshment = useMemo(
    () => refreshmentDrinkOffer({ state, context, drinks, guests }),
    [state, context, drinks, guests],
  )
  /* Run 19 §16: the coffee offer is priced out of the register, not from a
     catalogue row, so it takes no `drinks`. */
  const coffee = useMemo(
    () => coffeeOffer({ state, context, guests }),
    [state, context, guests],
  )
  /* F7: the answer only counts while the question is on the page. */
  const coffeeAsked = coffee !== null && state.coffee
  /* Run 19 §16: coffee is no longer a drink line. F3's conference
     refreshment water still is, and is the only one left. */
  const computedDrinks = useMemo(
    () => [refreshment].filter((entry) => entry !== null),
    [refreshment],
  )
  /* §16's two offer lines, inside the order total — which is also what
     makes them count towards §15's minimum. */
  const coffeeLines = useMemo(
    () => coffeeServiceLines({ offer: coffee, asked: coffeeAsked, params }),
    [coffee, coffeeAsked, params],
  )
  const limits = useMemo(
    () => (occasion === null ? null : limitsOf(occasion, guests, params)),
    [occasion, guests, params],
  )

  const resolved = useMemo(
    () =>
      occasion === null || limits === null
        ? null
        : resolveSelection({ state, available, occasion, limits, lane: calendar.lane, params }),
    [state, available, occasion, limits, calendar.lane, params],
  )

  /* Run 17 item B11(a): on *Állófogadás* the meal answer is pre-selected by
     the start time, from `meal_default_from`. It follows the clock while the
     customer has not answered for themselves; the moment they do,
     `mealTouched` is set and this leaves their answer alone. */
  const mealFrom = param(MEAL_DEFAULT_FROM_PARAM)
  useEffect(() => {
    if (!isReception(state.occasion) || state.mealTouched) return
    const wanted = occasionForMeal(mealReplacementDefault(state.start, mealFrom))
    if (wanted !== state.occasion) set({ occasion: wanted })
  }, [state.occasion, state.mealTouched, state.start, mealFrom, set])

  /* vB6 writes the resolved selection straight back into its one mutable
     state. Here the resolution is a pure function and the write-back is an
     effect that only fires when the answer differs — the second pass is a
     no-op, so it settles in one extra render rather than looping. */
  const lastWrite = useRef<string>('')
  useEffect(() => {
    if (resolved === null) return
    const signature = JSON.stringify([
      resolved.selection,
      resolved.key,
      resolved.limitKey,
      resolved.clipped,
      resolved.quantities,
    ])
    if (signature === lastWrite.current) return
    const unchanged =
      state.selection !== null &&
      state.selectionKey === resolved.key &&
      state.clipLimitKey === resolved.limitKey &&
      state.clipped === resolved.clipped &&
      JSON.stringify(state.selection) === JSON.stringify(resolved.selection) &&
      JSON.stringify(state.quantities) === JSON.stringify(resolved.quantities)
    if (unchanged) return
    lastWrite.current = signature
    set({
      selection: resolved.selection,
      selectionKey: resolved.key,
      clipLimitKey: resolved.limitKey,
      clipped: resolved.clipped,
      quantities: resolved.quantities,
      /* Run 15 item 1(e): the sentence belongs under the menu only while a
         changed dish is there to hold its quantity. */
      ...(Object.keys(resolved.quantities).length === 0 ? { quantitiesHeld: false } : {}),
      /* A fresh recommendation is not a selection the customer has touched. */
      ...(resolved.preselected
        ? { touched: { sav: false, sw: false }, sections: [], removed: null }
        : {}),
    })
  }, [resolved, state, set])

  /**
   * Run 15 item 1(e): the headcount, the appetite or the balance moved. Every
   * dish the customer has **not** changed is worked out again from the new
   * answer; every dish they have changed keeps the exact quantity they set.
   * One sentence under the menu says so, and only while there is a changed
   * dish to say it about.
   */
  const lastAnswers = useRef<string>('')
  useEffect(() => {
    const answers = [state.guestsField, state.profile, state.balance].join('|')
    const first = lastAnswers.current === ''
    const moved = !first && answers !== lastAnswers.current
    lastAnswers.current = answers
    if (!moved) return
    if (Object.keys(state.quantities).length === 0) return
    if (state.quantitiesHeld) return
    set({ quantitiesHeld: true })
  }, [state.guestsField, state.profile, state.balance, state.quantities, state.quantitiesHeld, set])

  const priced = useMemo(
    () =>
      occasion === null || resolved === null
        ? null
        : priceThePage({
            state,
            context,
            available,
            fruitAvailable,
            occasion,
            params,
            selection: resolved.selection,
            quantities: resolved.quantities,
            computedDrinks,
          }),
    [state, context, available, fruitAvailable, occasion, params, resolved, computedDrinks],
  )

  const totals = useMemo(
    () =>
      priced === null
        ? null
        : totalsOf({
            state,
            context,
            params,
            priced: priced.priced,
            slots,
            guests: priced.guests,
            services: coffeeLines,
          }),
    [state, context, params, priced, slots, coffeeLines],
  )

  const rules = useMemo(
    () =>
      priced === null || totals === null
        ? null
        : rulesOf({
            state,
            context,
            calendar,
            page: priced,
            totals,
            slots,
            t,
            money: (value: number) => f.money(value),
            coffeeAsked,
          }),
    [state, context, calendar, priced, totals, slots, t, f, coffeeAsked],
  )

  const basketPayload = useCallback(
    (): OrderPayload => ({
      event: {
        name: state.eventName.trim(),
        date: state.date,
        start: state.start,
        occasion: state.occasion,
        serving_window: state.servingWindow,
        guest_profile: state.profile,
        balance: state.balance,
        guests,
        gf_guests: Math.min(Math.max(state.glutenFreeGuests, 0), guests),
        dietary_note: state.dietaryNote.trim(),
        /* `build/FIXES.md` item 73 (F2): the server builds the conference out
           of the same three answers, or it would price a different day from
           the one on screen. Sent for every occasion and read for one. */
        conference: { ...state.conference },
        /* `build/FIXES.md` item 78 (F7): the coffee answer, and only while
           the question was there to answer. */
        coffee: coffeeAsked,
      },
      fulfilment: {
        kind: state.fulfilment,
        slot_id: slots.some((slot) => slot.slot_id === state.slotId)
          ? state.slotId
          : (slots[slots.length - 1]?.slot_id ?? ''),
        postcode: state.postcode.trim(),
      },
      basket: {
        savoury: resolved?.selection.sav ?? [],
        sweet: resolved?.selection.sw ?? [],
        /* Run 15 item 1(j): the quantities the customer set, so the server
           prices the order it is looking at and stores those lines as the
           customer's. Every other line is still the server's own engine run
           against `public.submission_context()` (§19.2 principle 2). */
        quantities: Object.entries(resolved?.quantities ?? {}).map(([dish_id, portions]) => ({
          dish_id,
          portions,
        })),
        fruit: state.fruit,
        /* Run 15 item 2(a): a drink is a whole quantity now, in bottles. */
        drinks: Object.entries(state.drinks)
          .filter(([, drink]) => drink.on)
          .map(([id, drink]) => ({ dish_id: id, portions: drink.bottles })),
      },
      customer: {
        is_business: state.business,
        last_name: state.lastName.trim(),
        first_name: state.firstName.trim(),
        email: state.email.trim(),
        phone: state.phone.trim(),
        company: state.company.trim(),
        vat_number: state.vatNumber.trim(),
      },
      billing_address: {
        postcode: state.billing.postcode.trim(),
        town: state.billing.town.trim(),
        street_and_number: state.billing.street.trim(),
        floor_and_door: state.billing.floor.trim(),
      },
      delivery_same_as_billing: state.sameAddress,
      delivery_address: state.sameAddress
        ? null
        : {
            postcode: state.shipping.postcode.trim(),
            town: state.shipping.town.trim(),
            street_and_number: state.shipping.street.trim(),
            floor_and_door: state.shipping.floor.trim(),
          },
      acceptance: { terms: state.acceptTerms, cancellation: state.acceptCancellation },
      /* `build/FIXES.md` item 79 (F8): the comment, trimmed. The server reads
         it for the `customer_comment` confirmation reason and stores it on
         the order; nothing the customer is shown is built from it here. */
      customer_comment: state.comment.trim(),
      enquiry_ticks: {
        staff_or_equipment: state.staff,
        custom_menu: state.custom,
        planteen_only: state.planteenOnly,
      },
      // Card payment decision 1 (DECIDED 20261101): one method under the
      // díjbekérő route. The server decides the lane either way.
      payment_method: simplePayRoute({ param, state }) ? state.payment : 'transfer',
      lang: state.lang,
      /* Display only. submit_order records a disagreement and acts on its own
         figure (§19.2 principle 2). */
      client_gross: totals?.total.gross,
    }),
    [state, slots, resolved, totals, guests, coffeeAsked],
  )

  /* §8.3: the short-notice enquiry, which `build/FIXES.md` item 79 leaves
     exactly as it was — and which is now the only thing that sets this. */
  const enquiry = (rules?.enquiry.length ?? 0) > 0
  /* `build/FIXES.md` item 79 (F8): at least one of the seven confirmation
     reasons applies, so the order is priced in full, sent without payment and
     waits for a colleague. */
  const confirm = (rules?.confirmation.length ?? 0) > 0
  const locked = state.sent !== null

  /**
   * DR-6 (`build/FIXES.md` item 9), option B: once the order is sent nothing
   * on the page may move, or the Összesítés above the confirmation goes on
   * recomputing and the page shows two different orders at once.
   *
   * This is vB6's `lockIfDone()`, called after every render for the same
   * reason vB6 calls it from `renderAll()`: the lock has to survive the one
   * thing that is still allowed to redraw the page. Threading a `disabled`
   * prop through forty controls is the other way of writing it, and the way
   * that quietly misses one — which is what it did here, on the whole of step
   * 01, until `apps/web/e2e/fixes.spec.ts` caught it.
   *
   * Two things are deliberately left alone: the language switch, because the
   * confirmation is redrawn from the record and has to be able to follow it,
   * and anything inside the open photograph, or a customer who submitted with
   * one up could not get rid of it.
   */
  useEffect(() => {
    if (!locked) return
    for (const element of document.querySelectorAll<HTMLInputElement>(
      'input, select, textarea, button',
    )) {
      if (element.id === 'lang' || element.closest('#zoom') !== null) continue
      element.disabled = true
    }
    /* A dish row is a div, so `disabled` means nothing to it: it needs
       aria-disabled for the click guard and tabindex -1 so a keyboard
       reaches the confirmation instead of walking through thirty rows that
       no longer do anything. */
    for (const row of document.querySelectorAll<HTMLElement>(
      '#dishList .pick, #fruitList .pick',
    )) {
      row.setAttribute('aria-disabled', 'true')
      row.tabIndex = -1
    }
  })

  const submit = useCallback(() => {
    if (state.submitting || priced === null || totals === null) return
    set({ submitting: true, submitError: null })
    const payload = basketPayload()
    const fullName = `${state.lastName.trim()} ${state.firstName.trim()}`.trim()

    const finish = (): void => set({ submitting: false })

    if (enquiry) {
      void submitEnquiry(config, {
        phone: state.phone.trim(),
        email: state.email.trim(),
        name: fullName,
        note: state.dietaryNote.trim(),
        routed_by: 'customer_page',
        ticks: payload.enquiry_ticks as Record<string, boolean>,
        event_params: payload.event as Record<string, unknown>,
        basket: [payload.basket],
      })
        .then((answer) => {
          if (answer.ok !== true) {
            set({ submitError: sentenceFor(answer) })
            return
          }
          set({
            sent: {
              /* §5.9 gives an enquiry no reference of its own. The id is what
                 staff see on it, so it is what the customer is given; rev8
                 should settle a human-readable one. Logged for Barna. */
              ref: answer.enquiry_id ?? '—',
              enquiry: true,
              confirmation: false,
              phone: state.phone.trim(),
              email: state.email.trim(),
              payment: simplePayRoute({ param, state }) ? state.payment : 'transfer',
              reviewed: false,
              pickup: state.fulfilment === 'pickup',
              quoted: totals.total.delivery.quoted,
              gross: totals.total.gross,
              slot: '',
              eventAt: calendar.eventDate?.getTime() ?? null,
              handoverAt: calendar.handoverDate?.getTime() ?? null,
              publicToken: null,
              next: null,
            },
          })
        })
        .finally(finish)
      return
    }

    void submitOrder(config, payload)
      .then((answer) => {
        if (answer.outcome === 'catalogue_changed') {
          set({ submitError: MENU_CHANGED })
          reloadMenu()
          return
        }
        if (answer.ok !== true || answer.order === undefined) {
          set({ submitError: sentenceFor(answer) })
          return
        }
        set({
          sent: {
            ref: answer.order.ref,
            enquiry: false,
            /* `build/FIXES.md` item 79 (F8): read off the **server's**
               answer, not off the page's own `confirm`. §19.2 principle 2 —
               the server decides — and the two could disagree if a figure
               moved between the last render and the submission. */
            confirmation: answer.confirmation === true,
            phone: state.phone.trim(),
            email: state.email.trim(),
            payment: simplePayRoute({ param, state }) ? state.payment : 'transfer',
            reviewed: answer.review === true,
            pickup: state.fulfilment === 'pickup',
            quoted: totals.total.delivery.quoted,
            gross: answer.order.gross ?? totals.total.gross,
            /* Run 16 item 2: the window the food changes hands in, collection
               as well as delivery, so the confirmation line can print the date
               and the weekday with it through the one shared formatter. */
            slot: fulfilmentWindow(state, slots, calendar, param),
            eventAt: calendar.eventDate?.getTime() ?? null,
            handoverAt: calendar.handoverDate?.getTime() ?? null,
            publicToken: answer.order.public_token,
            next: answer.payment?.next ?? null,
          },
        })
        /* `build/FIXES.md` item 79 (F8), ruling 9: **no reservation, no
           capture, no top-up, no invoice and no refund happen before
           confirmation.** So an order the server routed to
           `awaiting_confirmation` starts no payment at all: the customer pays
           from the link in U22, once a colleague has confirmed the price.
           Read off the server's answer, as `confirmation` above is. */
        if (answer.confirmation === true) return

        /* §9.4 steps 1 and 2. The order is placed; now the payment starts.
           `payment_start` writes the reservation and answers with the page to
           send the customer to -- SimplePay's own, or the mock driver's,
           depending on which driver is configured. The page never builds that
           URL itself and never knows which driver answered.

           On the transfer lane the same call moves the order to
           awaiting_payment and queues the díjbekérő; there is nowhere to go
           and the confirmation already says the bank details follow (§9.6). */
        const token = answer.order.public_token
        if (token === null || token === undefined) return
        void startPayment(config, token, fetch, { simplepayNotice: state.acceptSimplePay }).then((payment) => {
          if (payment.ok && payment.next === 'redirect_to_payment_page' && payment.payment_url) {
            window.location.assign(payment.payment_url)
            return
          }
          if (payment.ok) return
          /* §9.6: "Reservation fails -> Unchanged; retry." The order exists
             and holds its capacity; what failed is the start of the payment.
             The customer is told, on the confirmation they are already
             looking at, and the link to try again is there. */
          set({ paymentError: true })
        })
      })
      .finally(finish)
  }, [
    state,
    priced,
    totals,
    enquiry,
    config,
    basketPayload,
    calendar,
    slots,
    navigate,
    set,
    reloadMenu,
  ])

  const sendCallback = useCallback(() => {
    const phone = (state.callbackTouched ? state.callbackField : state.phone).trim()
    if (!phoneOk(phone)) {
      set({ callbackBad: true })
      document.getElementById('cbTel')?.focus()
      return
    }
    const payload = basketPayload()
    void submitCallback(config, {
      phone,
      email: state.email.trim(),
      name: `${state.lastName.trim()} ${state.firstName.trim()}`.trim(),
      note: state.dietaryNote.trim(),
      event_date: state.date === '' ? null : state.date,
      event_params: payload.event as Record<string, unknown>,
      basket: [payload.basket],
      // Run 12 item B: U17 and U18 answer in the page's language.
      lang: state.lang,
    }).then((answer) => {
      if (answer.ok !== true) {
        set({ submitError: sentenceFor(answer), callbackBad: true })
        return
      }
      set({
        callbackDone: {
          phone,
          /* §5.7: the request lapses after `callback_expiry_bd` business days
             or at the freeze point, whichever comes first. The server works
             that out and sends it back; the page does not count business days
             of its own. */
          expiresAt:
            answer.expires_at === undefined
              ? Date.now()
              : new Date(answer.expires_at).getTime(),
        },
        callbackOpen: false,
        callbackBad: false,
      })
    })
  }, [state, config, basketPayload, set])

  /* Specification §3.1 (DECIDED 20261023): the missing-data notice's items
     with their fields, and the jump to one. On one column the offer sheet
     may be open over the page, so it closes first and the jump waits for
     that render. */
  const missing = missingItems(state, t, enquiry)
  const goToField = (field: string): void => {
    set({ offerOpen: false })
    requestAnimationFrame(() => {
      jumpToField(field)
    })
  }
  const goToFirstMissing = (): boolean => {
    const field = firstMissingField(missing, state.lang)
    if (field === null) return false
    goToField(field)
    return true
  }
  /* A field a jump marked invalid is unmarked once it is filled. */
  useEffect(() => clearSettledFlags(missing))

  if (occasion === null || limits === null || resolved === null || priced === null || totals === null || rules === null) {
    return <Loading lang={state.lang} error={null} />
  }

  const bundle: PageBundle = {
    t,
    f,
    state,
    set,
    context,
    stale,
    params,
    param,
    num,
    flag,
    occasion,
    dishes,
    available,
    fruitAvailable,
    drinks,
    groups,
    groupMembers: (groupKey) =>
      new Set(dishes.filter((dish) => dish.group === groupKey).map((dish) => dish.id)),
    calendar,
    slots,
    deliveryBlocked: deliveryBlocked(state, slots),
    towns,
    missing,
    ticks: sectionTicks({
      state,
      missing,
      ready: offerIsReady(state, calendar) || hasHeadcount(state.guestsField),
      dateOk: calendar.ok,
      slotOk: slots.length > 0,
      deliverable: !outsideDeliveryRadius(context),
      enquiry,
    }),
    goToField,
    goToFirstMissing,
    limits,
    selection: resolved.selection,
    quantities: resolved.quantities,
    clipped: resolved.clipped,
    priced,
    totals,
    rules,
    menuCheck: menuCheckOf(priced, menuCheckBand),
    enquiry,
    confirm,
    refreshment,
    coffee,
    photos: flag('menu_show_photos'),
    overview: flag('menu_show_overview'),
    /* DR-6 (build/FIXES.md item 9), option B: once the order is sent nothing
       on the page may move, or the Összesítés above the confirmation goes on
       recomputing and the page shows two different orders at once. The
       language switch is the one control left live, because the panel is
       redrawn from the record and has to be able to follow it; so is the
       close button of an open photograph. */
    locked,
    submit,
    sendCallback,
    /* DR-6: a locked page opens no photograph. vB6 guards this at the top of
       its one click handler; here the guard is where the dialog is opened,
       which is the same place and the only one. Closing an already-open one
       stays possible, or a customer who submitted with the zoom up could not
       get rid of it. */
    openZoom: (dishId) => {
      if (locked) return
      set({ zoom: dishId })
    },
    closeZoom: () =>
      setStateAndRestoreFocus(set, state.zoom, () => {
        set({ zoom: null })
      }),
  }

  /* Fix list item 4 (Barna, 10 October 2026): until there is a headcount
     there is no offer and no amount anywhere — the menu and the summary
     say what to do instead. The sections keep their ids so the rail still
     jumps to them. Gated on the headcount alone: a customer who clears the
     date keeps 04, where *Nincs megadva dátum.* says what is missing. */
  const ready = offerIsReady(state, calendar) || hasHeadcount(state.guestsField)

  return (
    <>
      <Header page={bundle} />
      <Banner page={bundle} />
      {/* Redesign (DECIDED 20261101): the black opening band and the yellow
          trust bar run the full width; the strip and the step pills head
          the two-column stage of panels and the summary card. */}
      <Hero page={bundle} />
      <TrustBar page={bundle} />
      <div className="wrap">
        <Strip page={bundle} />
        <Rail page={bundle} />
        <main>
          <Step1Event page={bundle} />
          {ready ? <Step2Menu page={bundle} /> : <NotReady page={bundle} step={2} />}
          <Step3Contact page={bundle} />
          {ready ? <Step4Summary page={bundle} /> : <NotReady page={bundle} step={4} />}
        </main>
        {/* `build/FIXES.md` item 76 (F5): the offer card takes the right
            column the §5.4 side summary had, and on one column the stylesheet
            orders it straight after step 01 — event parameters, offer, menu
            editor, contact and payment. */}
        <OfferCard page={bundle} />
      </div>
      {/* Legal pages brief, item 3: the footer of every customer page. */}
      <LegalFooter lang={state.lang} barClear />
      <OfferBar page={bundle} />
      <Zoom page={bundle} />
    </>
  )
}

/** Fix list item 4: the customer has typed a headcount of at least one. */
const hasHeadcount = (field: string): boolean => field.trim() !== '' && Number(field) >= 1

/** Fix list item 4: step 02 or 04 before there is anything to price. */
function NotReady(props: { page: PageBundle; step: 2 | 4 }): JSX.Element {
  const { t } = props.page
  return (
    <section id={`sec${props.step}`} data-testid={`sec${props.step}-waiting`}>
      <div className="sechead">
        <span className="n">0{props.step}</span>
        <h2>{props.step === 2 ? t('Menü', 'Menu') : t('Összesítés', 'Summary')}</h2>
      </div>
      <div className="card">
        <p style={{ margin: 0 }}>
          {props.step === 2
            ? t(
                'Adja meg a vendégek számát az 1. lépésben, és összeállítjuk a menüt.',
                'Enter the number of guests in step 1 and we will put the menu together.',
              )
            : t(
                'Az összesítés a vendégek számának megadása után jelenik meg.',
                'The summary appears once you have entered the number of guests.',
              )}
        </p>
      </div>
    </section>
  )
}

/**
 * `build/FIXES.md` item 29: closing the photograph gives focus back to the
 * dish row it was opened from. The row is looked up when the dialog closes,
 * not held from when it opened: the list is rebuilt on every render and a
 * node kept from opening time is detached the moment anything else moves —
 * the language switch being exactly that.
 */
function setStateAndRestoreFocus(
  _set: (patch: Patch) => void,
  dishId: string | null,
  close: () => void,
): void {
  close()
  if (dishId === null) return
  requestAnimationFrame(() => {
    const row = document.querySelector<HTMLElement>(
      `[data-pick="${dishId}"],[data-gfauto="${dishId}"],[data-fruit="${dishId}"]`,
    )
    row?.focus()
  })
}
